import React, { useRef, useState } from "react";
import styled, { css } from "styled-components";
import { CustomIcon } from "components/icon/customIcon.component";
import { ColorV2 } from "config/colors";
import { TypographyV2 } from "config/font";

export interface CollapsibleSectionProps {
  label: string;
  isOpen?: boolean;
  defaultOpen?: boolean;
  onToggle?: (open: boolean) => void;
  children?: React.ReactNode;
  headerRight?: React.ReactNode;
}

let collapsibleSectionId = 0;

/** Collapsible section with header row and optional content. */
export const CollapsibleSection = ({
  label,
  isOpen,
  defaultOpen = false,
  onToggle,
  children,
  headerRight,
}: CollapsibleSectionProps): JSX.Element => {
  const [internalOpen, setInternalOpen] = useState(defaultOpen);
  const resolvedOpen = isOpen ?? internalOpen;
  const contentIdRef = useRef<string>();
  if (!contentIdRef.current) {
    collapsibleSectionId += 1;
    contentIdRef.current = `collapsible-section-${collapsibleSectionId}`;
  }
  const contentId = contentIdRef.current;

  const handleToggle = (): void => {
    const next = !resolvedOpen;
    if (isOpen === undefined) {
      setInternalOpen(next);
    }
    onToggle?.(next);
  };

  return (
    <Wrapper $isOpen={resolvedOpen}>
      <Header
        type="button"
        onClick={handleToggle}
        $isOpen={resolvedOpen}
        aria-expanded={resolvedOpen}
        aria-controls={contentId}
      >
        <HeaderLeft>
          <Label>{label}</Label>
        </HeaderLeft>
        <HeaderRight>
          {headerRight}
          <Chevron $isOpen={resolvedOpen}>
            <CustomIcon
              name="v2/down-chevron"
              size="24px"
              color={ColorV2.Blue2}
            />
          </Chevron>
        </HeaderRight>
      </Header>
      {resolvedOpen && <Body id={contentId}>{children}</Body>}
    </Wrapper>
  );
};

const Wrapper = styled.div<{ $isOpen: boolean }>`
  width: 100%;
  display: flex;
  flex-direction: column;
  background: ${ColorV2.Blue7};
  border-radius: 16px;
  overflow: hidden;
`;

const Header = styled.button<{ $isOpen: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 24px;
  height: 56px;
  border: none;
  background: ${ColorV2.Blue7};
  cursor: pointer;
  text-align: left;

  ${(props) =>
    props.$isOpen &&
    css`
      border-bottom-left-radius: 0;
      border-bottom-right-radius: 0;
    `}
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding-right: 56px;
  min-width: 0;
`;

const HeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const Label = styled.span`
  ${TypographyV2.h5};
  color: ${ColorV2.Blue1};
  opacity: 0.5;
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const Chevron = styled.span<{ $isOpen: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.2s ease;

  ${(props) =>
    props.$isOpen &&
    css`
      transform: rotate(180deg);
    `}
`;

const Body = styled.div`
  background: ${ColorV2.Blue7};
  padding: 16px 24px;
  padding-right: 56px;
`;
